<template>
  <div>
    <div>
      <div class="tw-flex tw-justify-between tw-mb-5">
        <div class="tw-flex tw-flex-col">
          <div class="tw-flex">
            <div class="tw-text-gray-800 tw-font-normal">{{ prescription.businessProgram.name }}</div>
            <base-badge class="tw-ml-2 tw-my-auto" color="primary" size="sm">{{ getPrescriptionStatus(prescription.status.value) }}</base-badge>
          </div>
          <div class="tw-text-gray-600 tw-text-sm tw-mt-1">{{ getLocation(prescription) }}</div>
        </div>
        <base-dropdown class="tw-my-auto" :options="actionOptions" :showBackground="false" align="right">
          <base-icon>
            <more-vertical-icon class="tw-w-6 tw-h-6" />
          </base-icon>
          <template #option="{ option: label }">
            <button class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none" @click="actionLink(label)">
              {{ label }}
            </button>
          </template>
        </base-dropdown>
      </div>
      <div>
        <loading :active="isPDFRequesting" :color="'#ff0090'" :loader="'bars'" :is-full-page="false"></loading>
        <edit-business-member-prescription-medications
          v-if="isEditing"
          :edit-medications.sync="editMedications"
          :allowed-medications="allowedMedications"
          :max-medications="prescription.businessProgram.maxMedications"
          @cancel="isEditing = false"
          @save="saveNewMedications"
          :is-saving="isSaving"
        />
        <div v-else class="tw-pb-2">
          <div v-for="(medication, index) in orderedMedications" :key="index" class="tw-mb-3">
            <div class="tw-text-xs tw-font-medium tw-text-gray-800">Medication {{ index + 1 }}</div>
            <div class="tw-text-sm tw-text-gray-600">{{ medication.businessItemCategory.name.value }}</div>
          </div>
        </div>
        <div class="tw-w-full tw-border-gray-200 tw-border-t"></div>
        <business-member-prescription-notes
          :notes="prescription.notes"
          class="tw-mt-4"
          :business-prescription-id="prescription.id"
          :user-id="userId"
          :businessId="businessId"
          @updated="$emit('updated')"
        />
      </div>
    </div>
    <business-member-prescription-change-status ref="changeStatusModal" :businessId="businessId" :businessPrescriptionId="prescription.id" @updated="$emit('updated')" />
    <business-member-prescription-delete-program ref="deleteProgramModal" :businessId="businessId" :businessPrescriptionId="prescription.id" @updated="$emit('updated')" />
  </div>
</template>

<script>
import BusinessMemberPrescriptionNotes from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionNotes";
import BusinessMemberPrescriptionChangeStatus from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionChangeStatus";
import BusinessMemberPrescriptionDeleteProgram from "@/views/business/member/prescriptions/components/BusinessMemberPrescriptionDeleteProgram";
import EditBusinessMemberPrescriptionMedications from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionMedications";
import { mapGetters } from "vuex";
import { capitalize, orderBy, uniq } from "lodash";
import { MoreVerticalIcon } from "@/assets/svgs";
import { PRESCRIPTION_STATUS_OPTIONS } from "@/constants/constants";

export default {
  components: {
    BusinessMemberPrescriptionNotes,
    BusinessMemberPrescriptionChangeStatus,
    BusinessMemberPrescriptionDeleteProgram,
    EditBusinessMemberPrescriptionMedications,
    MoreVerticalIcon,
  },
  props: {
    businessId: String,
    userId: String,
    prescription: Object,
  },
  data() {
    return {
      capitalize,
      newStatus: null,
      changeReason: null,
      isPDFRequesting: false,
      isLoading: false,
      isEditing: false,
      isSaving: false,
      editMedications: [],
      deleteError: { errorTitle: "", errorMessage: "", error: null },
      changeError: {
        errorTitle: "",
        errorMessage: "",
        error: null,
      },
      numMedications: this.prescription.medications.length,
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isLocationFiltered", "isPredictile", "isBusinessManagement", "isBusinessAdministrator"]),
    actionOptions() {
      let options = [];
      if (!this.prescription.status.isAccepted && this.isLocationFiltered === false) options.push("Invite to Plan");
      if (!this.prescription.status.isAccepted && this.isLocationFiltered === false) options.push("Create Plan");
      if (this.isLocationFiltered === false) options.push("Change Status");
      if (this.isLocationFiltered === false) options.push("Generate Rx Details PDF");
      if (
        !this.isEditing &&
        (this.isBusinessManagement(this.organization.id) === true || this.isBusinessAdministrator(this.organization.id) === true || this.isPredictile() === true)
      )
        options.push("Edit");
      if (this.canDelete === true && this.isLocationFiltered === false) options.push("Delete");

      return options;
    },
    allowedMedications() {
      return this.prescription.businessProgram.allowedMedications
        .map((am) => ({
          label: am.name.value,
          value: am.id,
          productGroup: am.productGroup,
        }))
        .filter((m) => !this.editMedications.find((em) => em.value === m.value));
    },
    sortedAllowdMedications() {
      if (!this.allAllowedMedications) return [];

      const groups = orderBy(uniq(this.allAllowedMedications.map((m) => m.productGroup)));

      return groups.map((g) => ({
        label: g,
        options: orderBy(this.allAllowedMedications.filter((m) => m.productGroup === g), ["productGroup", "label"]),
      }));
    },
    orderedMedications() {
      return orderBy(this.prescription.medications, [(x) => x.sortOrder, (x) => x.businessItemCategory.name.value]);
    },
    canDelete() {
      if (this.isPredictile() === true || this.isBusinessManagement(this.businessId) === true || this.isBusinessAdministrator(this.businessId) === true) return true;
      else return false;
    },
    moreAllowedMedications() {
      return this.prescription.businessProgram.maxMedications > this.numMedications;
    },
  },
  watch: {
    prescription(newValue) {
      this.numMedications = newValue.medications.length;
    },
    value() {
      this.selectedMedicationId = this.value && this.value.businessItemCategory ? this.value.businessItemCategory.id : null;
    },
  },
  methods: {
    getLocation(prescription) {
      const location = this.locations.find((l) => l.businessLocationId === prescription.businessLocationId);
      return location ? location.name : "";
    },
    actionLink(label) {
      switch (label) {
        case "Invite to Plan":
          this.inviteToPlan();
          break;
        case "Create Plan":
          this.createNewPlan();
          break;
        case "Change Status":
          this.changeStatus();
          break;
        case "Generate Rx Details PDF":
          this.generateMemberPDF();
          break;
        case "Delete":
          this.del();
          break;
        case "Edit":
          this.startEditing();
      }
    },
    inviteToPlan() {
      this.$emit("show-invite-modal", this.prescription);
    },
    createNewPlan() {
      this.$emit("show-create-modal", this.prescription);
    },
    changeStatus() {
      this.$refs.changeStatusModal.show(this.prescription.status.value);
    },
    del() {
      this.$refs.deleteProgramModal.show(this.prescription.businessProgram.name);
    },
    generateMemberPDF() {
      this.isPDFRequesting = true;
      this.$axios
        .post(`/businesses/${this.businessId}/users/${this.userId}/prescriptions/${this.prescription.id}/rxdetails/pdf`)
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "PDF Queued",
            message: `The PDF for ${this.prescription.businessProgram.name} was requested. This may take a minute to add the to the Member Documents tab.`,
          });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error requesting PDF.`;
          this.errorMessage = "Sorry, we had trouble submitting the request this PDF. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isPDFRequesting = false;
        });
    },
    getPrescriptionStatus(status) {
      return PRESCRIPTION_STATUS_OPTIONS.find((s) => s.value === status).label;
    },
    startEditing() {
      this.isEditing = true;
      this.editMedications = this.orderedMedications.map((m) => ({
        label: m.businessItemCategory.name.value,
        value: m.businessItemCategory.id,
        productGroup: m.businessItemCategory.productGroup,
      }));
    },
    async saveNewMedications() {
      const oldMedications = this.orderedMedications.map((m) => m.businessItemCategory.id).filter((m) => m);
      const newMedications = this.editMedications.map((m) => m.value).filter((m) => m);
      const changesMade = oldMedications.length !== newMedications.length || oldMedications.some((m, i) => m !== newMedications[i]);

      if (changesMade) {
        try {
          this.isSaving = true;
          await this.$axios.post(`/businesses/${this.organization.id}/members/${this.userId}/prescriptions/medications`, {
            businessPrescriptionId: this.prescription.id,
            businessItemCategoryIds: newMedications,
          });
          this.isEditing = false;
          this.$emit("updated");
        } catch (error) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error saving medications",
            message: "We had a problem updating these medications. Please reload the page to try again.",
            error,
          });
          throw error;
        } finally {
          this.isSaving = false;
        }
      } else {
        this.isEditing = false;
      }
    },
  },
};
</script>
